import type { ReactNode } from 'react';

import { SelectedItemChips } from '~/chords/Select/components/MultiSelect/components/SelectedItemChips';
import { useMultiSelect } from '~/chords/Select/components/MultiSelect/useMultiSelect';

export interface RenderDropdownProps<T> {
  onSelect: (item: T) => void;
  selectedValues: ReadonlySet<string>;
  resetKey: number;
  disabled: boolean;
}

export interface MultiSelectProps<T> {
  value: T[];
  onValueChange: (items: T[]) => void;
  getItemLabel: (item: T) => string;
  getItemValue: (item: T) => string;
  renderDropdown: (props: RenderDropdownProps<T>) => ReactNode;
  disabled?: boolean;
}

export function MultiSelect<T>({
  value,
  onValueChange,
  getItemLabel,
  getItemValue,
  renderDropdown,
  disabled = false,
}: MultiSelectProps<T>) {
  const { selectedIds, resetKey, handleSelect, handleRemove } = useMultiSelect({
    selectedValues: value,
    onValueChange,
    getItemValue,
    disabled,
  });

  const chipItems = value.map((item) => ({
    value: getItemValue(item),
    label: getItemLabel(item),
  }));

  return (
    <div className="flex flex-col gap-sm">
      <SelectedItemChips
        items={chipItems}
        onRemove={handleRemove}
        disabled={disabled}
      />
      {renderDropdown({
        onSelect: handleSelect,
        selectedValues: selectedIds,
        resetKey,
        disabled,
      })}
    </div>
  );
}

MultiSelect.displayName = 'MultiSelect';
